import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, Printer, Ban, ChevronDown, Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useReceiptsList, useActiveReceiptCategories, useReceiptMutations, useReceipt } from '../hooks/useReceipts';
import { IssueReceiptForm } from '../components/IssueReceiptForm';
import { ReceiptPrintPreview } from '../components/ReceiptPrintPreview';
import { receiptToPrintData, formatReceiptNumber } from '../types';
import type { ReceiptStatus, ReceiptListItem } from '../types';
import { publicReceiptUrl } from '../utils/publicReceiptUrl';
import { formatMoney, formatShortDate } from '../utils/receiptFormat';
import { exportReceiptsExcel } from '../utils/exportReceipts';
import { getAvailableYears } from '@/lib/dateUtils';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const STATUS_LABELS: Record<ReceiptStatus, string> = { issued: 'Emitido', void: 'Anulado' };
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const PAGE_SIZE = 50;
const SELECT_CLASS =
  'appearance-none rounded-md border border-input bg-background py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

const normalize = (v: string) => v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

function numberOf(r: ReceiptListItem) {
  return formatReceiptNumber(r.number);
}

function StatusBadge({ status }: { status: ReceiptStatus }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
      status === 'issued' ? 'bg-success/15 text-success' : 'bg-destructive/15 text-destructive'
    }`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

export function ReceiptsListPage() {
  const currentYear = new Date().getFullYear();
  const { toast } = useToast();

  // Filters live in the URL so reload / back keep the view.
  const [params, setParams] = useSearchParams();
  const yearParam   = parseInt(params.get('year') ?? '', 10);
  const year        = yearParam >= 2021 && yearParam <= currentYear + 1 ? yearParam : currentYear;
  const categoryId  = params.get('categoryId');
  const statusParam = params.get('status');
  const status: ReceiptStatus | null = statusParam === 'issued' || statusParam === 'void' ? statusParam : null;
  const monthParam  = parseInt(params.get('month') ?? '', 10);
  const month       = monthParam >= 1 && monthParam <= 12 ? monthParam : null;
  const query       = params.get('q') ?? '';

  const updateParams = (patch: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v) next.set(k, v); else next.delete(k);
      }
      return next;
    }, { replace: true });
  };

  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<ReceiptListItem | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data: categories = [] } = useActiveReceiptCategories();
  // Category is filtered client-side (not in the query) so the dropdown can show a count per category.
  const { data: receipts = [], isLoading, error } = useReceiptsList({ year, categoryId: null, status });
  const { voidOne } = useReceiptMutations();
  const { data: printingReceipt } = useReceipt(printingId ?? undefined);

  const availableYears = getAvailableYears(currentYear);

  // Search and month are applied client-side over the already-loaded year.
  const searched = useMemo(() => {
    const q = normalize(query.trim());
    return receipts.filter((r) => {
      if (month && new Date(r.issued_at).getMonth() + 1 !== month) return false;
      if (!q) return true;
      return normalize(`${r.payer_name} ${r.payer_ci ?? ''} ${numberOf(r)}`).includes(q)
        || String(r.number) === q.replace(/^0+/, '');
    });
  }, [receipts, query, month]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of searched) {
      const id = r.category?.id;
      if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  }, [searched]);

  const filtered = useMemo(
    () => (categoryId ? searched.filter((r) => r.category?.id === categoryId) : searched),
    [searched, categoryId],
  );

  const totals = useMemo(() => {
    const sums: Record<string, number> = {};
    for (const r of filtered) {
      if (r.status === 'issued') sums[r.currency] = (sums[r.currency] ?? 0) + r.total;
    }
    return sums;
  }, [filtered]);

  // Back to the first page whenever the result set changes.
  useEffect(() => { setPage(0); }, [year, categoryId, status, month, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportReceiptsExcel(filtered, year);
    } catch (e) {
      console.error(e);
      toast({ title: 'No se pudo exportar', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const closeVoid = () => { setVoiding(null); setVoidReason(''); };

  const handleVoid = async () => {
    if (!voiding || !voidReason.trim()) return;
    try {
      await voidOne.mutateAsync({ id: voiding.id, reason: voidReason.trim() });
    } catch (e) {
      console.error(e);
      toast({ title: 'No se pudo anular el recibo', variant: 'destructive' });
      return;
    }
    toast({ title: `Recibo ${numberOf(voiding)} anulado` });
    closeVoid();
  };

  const sourceBadge = (r: ReceiptListItem) => r.source_type === 'tithe' && (
    <Link
      to="/tithes"
      onClick={(e) => e.stopPropagation()}
      className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:underline"
    >
      Diezmo
    </Link>
  );

  const actions = (r: ReceiptListItem) => (
    <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setPrintingId(r.id)}
        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        title="Ver / imprimir"
        aria-label={`Ver o imprimir recibo ${numberOf(r)}`}
      >
        <Printer className="size-4" />
      </button>
      {r.status === 'issued' && (
        <button
          onClick={() => setVoiding(r)}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          title="Anular"
          aria-label={`Anular recibo ${numberOf(r)}`}
        >
          <Ban className="size-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-card p-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Recibos</h1>
            <p className="text-sm text-muted-foreground">Emisión y control de recibos oficiales</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting || filtered.length === 0}
              className="flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              <span className="max-sm:hidden">Exportar Excel</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="size-4" />
              Emitir recibo
            </button>
          </div>
        </div>
      </div>

      <div className="border-b border-border bg-card px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <select
              value={year}
              onChange={(e) => updateParams({ year: e.target.value === String(currentYear) ? null : e.target.value })}
              className={SELECT_CLASS}
              aria-label="Año"
            >
              {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>

          <div className="relative">
            <select
              value={month ?? ''}
              onChange={(e) => updateParams({ month: e.target.value || null })}
              className={SELECT_CLASS}
              aria-label="Mes"
            >
              <option value="">Todos los meses</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>

          <div className="relative">
            <select
              value={categoryId ?? ''}
              onChange={(e) => updateParams({ categoryId: e.target.value || null })}
              className={SELECT_CLASS}
              aria-label="Categoría"
            >
              <option value="">Todas las categorías ({searched.length})</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({categoryCounts.get(c.id) ?? 0})</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>

          <div className="flex overflow-hidden rounded-md border border-input text-sm">
            {([null, 'issued', 'void'] as (ReceiptStatus | null)[]).map((s) => (
              <button
                key={s ?? 'all'}
                onClick={() => updateParams({ status: s })}
                className={`px-3 py-1.5 transition-colors ${status === s ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
              >
                {s === null ? 'Todos' : STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:ml-auto sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => updateParams({ q: e.target.value || null })}
              placeholder="Buscar pagador, C.I. o N°"
              className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-destructive">
            Error al cargar recibos.
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No hay recibos para los filtros seleccionados.
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {filtered.length} {filtered.length === 1 ? 'recibo' : 'recibos'}
              {Object.keys(totals).length > 0 && ' · Total emitido '}
              {Object.entries(totals)
                .map(([cur, sum]) => `${cur === 'USD' ? 'USD' : 'Bs.'} ${formatMoney(sum)}`)
                .join(' · ')}
            </p>

            {/* Phones: cards */}
            <div className="space-y-2 sm:hidden">
              {visible.map((r) => (
                <div
                  key={r.id}
                  onClick={() => setPrintingId(r.id)}
                  className="cursor-pointer rounded-lg border border-border bg-card p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-mono text-xs">{numberOf(r)}</p>
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="mt-1 font-medium">{r.payer_name}</p>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{formatShortDate(r.issued_at)}</span>
                    <span className="font-medium">{formatMoney(r.total)} {r.currency}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span>{sourceBadge(r)}</span>
                    {actions(r)}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden overflow-x-auto rounded-lg border border-border sm:block">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted/60">
                    <th className="border border-border/40 px-3 py-2 text-left font-semibold">N°</th>
                    <th className="border border-border/40 px-3 py-2 text-left font-semibold">Categoría</th>
                    <th className="border border-border/40 px-3 py-2 text-left font-semibold">Pagador</th>
                    <th className="border border-border/40 px-3 py-2 text-left font-semibold">Fecha</th>
                    <th className="border border-border/40 px-3 py-2 text-right font-semibold">Total</th>
                    <th className="border border-border/40 px-3 py-2 text-center font-semibold">Origen</th>
                    <th className="border border-border/40 px-3 py-2 text-center font-semibold">Estado</th>
                    <th className="border border-border/40 px-3 py-2 text-center font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.id} onClick={() => setPrintingId(r.id)} className="cursor-pointer hover:bg-muted/30">
                      <td className="border border-border/40 px-3 py-2 font-mono text-xs">{numberOf(r)}</td>
                      <td className="border border-border/40 px-3 py-2">{r.category?.name}</td>
                      <td className="border border-border/40 px-3 py-2">
                        <p>{r.payer_name}</p>
                        {r.payer_ci && <p className="text-xs text-muted-foreground">C.I. {r.payer_ci}</p>}
                      </td>
                      <td className="border border-border/40 px-3 py-2">{formatShortDate(r.issued_at)}</td>
                      <td className="border border-border/40 px-3 py-2 text-right font-medium">
                        {formatMoney(r.total)} {r.currency}
                      </td>
                      <td className="border border-border/40 px-3 py-2 text-center">{sourceBadge(r)}</td>
                      <td className="border border-border/40 px-3 py-2 text-center"><StatusBadge status={r.status} /></td>
                      <td className="border border-border/40 px-3 py-2">{actions(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pageCount > 1 && (
              <div className="flex items-center justify-end gap-3 text-sm">
                <span className="text-muted-foreground">Página {page + 1} de {pageCount}</span>
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  aria-label="Página anterior"
                  className="rounded-md border border-border p-1.5 hover:bg-muted disabled:opacity-40"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                  aria-label="Página siguiente"
                  className="rounded-md border border-border p-1.5 hover:bg-muted disabled:opacity-40"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <IssueReceiptForm
          onClose={() => setShowForm(false)}
          // Straight to the print preview so the new receipt can be handed over.
          onIssued={(r) => { setShowForm(false); setPrintingId(r.id); }}
        />
      )}

      {printingReceipt && (
        <ReceiptPrintPreview
          data={receiptToPrintData(printingReceipt, publicReceiptUrl(printingReceipt.public_token))}
          onClose={() => setPrintingId(null)}
        />
      )}

      <AlertDialog open={!!voiding} onOpenChange={(o) => { if (!o && !voidOne.isPending) closeVoid(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anular recibo {voiding && numberOf(voiding)}</AlertDialogTitle>
            <AlertDialogDescription>
              {voiding && <>{voiding.payer_name} · {formatMoney(voiding.total)} {voiding.currency}. </>}
              El número no se reutilizará. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <textarea
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            rows={3}
            placeholder="Motivo de anulación"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={voidOne.isPending}>Cancelar</AlertDialogCancel>
            <button
              onClick={handleVoid}
              disabled={!voidReason.trim() || voidOne.isPending}
              className="flex items-center justify-center gap-2 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {voidOne.isPending ? <Loader2 className="size-4 animate-spin" /> : <Ban className="size-4" />}
              Anular
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
